const axios = require('axios').default;
const Agent = require('agentkeepalive');
const { HttpsAgent } = require('agentkeepalive');
const safeStringify = require('fast-safe-stringify').default;
const axiosRetry = require('axios-retry');
const { isNetworkOrIdempotentRequestError } = require('axios-retry');

const httpConf = {
    maxSockets: 100,
    maxFreeSockets: 10,
    timeout: 60000,
    freeSocketTimeout: 30000, // free socket keepalive for 30 seconds
};
// @ts-ignore
const keepaliveAgentHttp = new Agent(httpConf);

const keepaliveAgentHttps = new HttpsAgent(httpConf);

/**
 * checks the conditions and logs the error if they pass
 * @param {import("axios").AxiosError} axiosError
 * @param {string} serviceName
 * @param {object} errorToLog
 * @param {import(".").tidaAxiosConfig} [tidalAxiosConfig]
 * @returns {void}
 */
function maybeLogError(axiosError, serviceName, errorToLog, tidalAxiosConfig) {
    const shouldLogError = tidalAxiosConfig && ((typeof tidalAxiosConfig.shouldLogError === 'function'
        && tidalAxiosConfig.shouldLogError(axiosError)) || (typeof tidalAxiosConfig.shouldLogError === 'boolean'
            && tidalAxiosConfig.shouldLogError));
    if (shouldLogError) {
        console.error(`${serviceName} logging error. AxiosError: ${safeStringify(errorToLog, undefined, 2)}`);
    }
}

/**
 * adds interceptors and loggers to axios
 * @type import('.').tidalAxios
 */
module.exports.tidalAxios = function(serviceName, tidalAxiosConfig) {
    const instance = axios.create({
        timeout: tidalAxiosConfig && typeof tidalAxiosConfig.timeout === 'number'
            ? tidalAxiosConfig.timeout : 5000, // 5 seconds
        //keepAlive pools and reuses TCP connections, so it's faster
        httpAgent: keepaliveAgentHttp,
        httpsAgent: keepaliveAgentHttps,
    });

    // only retry if shouldRetry is provided
    // @ts-ignore
    axiosRetry(instance, {
        retries: 3,
        retryCondition: /** @param {import("axios").AxiosError} error */ error => {
            const useRetryFunctionality = tidalAxiosConfig && ((typeof tidalAxiosConfig.shouldRetry === 'boolean'
                && tidalAxiosConfig.shouldRetry) || (typeof tidalAxiosConfig.shouldRetry === 'function'
                    && tidalAxiosConfig.shouldRetry(error)));

            return isNetworkOrIdempotentRequestError(error) && useRetryFunctionality;
        },
    });

    // request interceptor
    instance.interceptors.request.use(function(config) {
        // log the config before the request
        if (tidalAxiosConfig && tidalAxiosConfig.enableRequestLogging) {
            if (config) {
                const configToLog = {
                    params: config.params,
                    method: config.method,
                    baseURL: config.baseURL,
                    data: config.data,
                    headers: config.headers,
                    url: config.url,
                };
                // replace the passwords if any
                const logData = safeStringify(configToLog, undefined, 2).replace(/password=(.+?&)/, 'password=******&');
                console.info(`${serviceName} logging before request. AxiosConfig: ${logData}`);
            }
        }
        return config;
    }, function(error) {
        maybeLogError(error, serviceName, error, tidalAxiosConfig);

        return Promise.reject(error);
    });
    // response interceptor
    instance.interceptors.response.use(function(response) {
        return response;
    }, function(error) {
        const config = error.config;
        if (!config) {
            return Promise.reject(error);
        }

        const errorToLog = {
            method: config.method,
            baseURL: config.baseURL,
            data: error.response ? error.response.data : 'No axiosError.response. Possible timeout',
            status: error.response ? error.response.status : error.status,
            statusText: error.response ? error.response.statusText : 'No axiosError.response. Possible timeout',
            requestHeaders: config.headers,
            responseHeaders: error.response ? error.response.headers : 'No axiosError.response. Possible timeout',
            url: config.url,
            stack: error.stack,
            message: error.message,
        };

        maybeLogError(error, serviceName, errorToLog, tidalAxiosConfig);

        return Promise.reject(error);
    });
    return instance;
};
